import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import cl from 'classnames';
import _ from 'lodash';
import {ROUTES} from '../../configs/routes';
import {ArrowForwardIcon} from '../../components/SvgComponents/SvgComponents';
import {formatText} from '../../services/common';
import {t} from '../../services/i18nHandler';
import ModalVideoPlayer from '../../components/Modal/newTemplates/ModalVideoPlayer/ModalVideoPlayer';

const slideDuration = 15000;

const Timer = function (callback, delay) {
  let timerId = delay;
  let start = delay;
  let remaining = delay;

  this.pause = () => {
    window.clearTimeout(timerId);
    remaining -= Date.now() - start;
  };

  this.clear = () => {
    window.clearTimeout(timerId);
  };

  this.resume = () => {
    start = Date.now();
    window.clearTimeout(timerId);
    timerId = window.setTimeout(callback, remaining);
  };

  this.resume();
};

class LandingPageAboutSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      slide: 0,
      isActive: false,
      videoSrc: null,
    };
    this.timer = null;
    this.scrollListener = _.debounce(this.onScroll, 500);
    this.containerRef = React.createRef();
    this.textContainerRef = React.createRef();
    this.xDown = null;
    this.onTochMove = _.debounce(this.handleTouchMove, 300);
  }

  componentDidMount() {
    if (this.textContainerRef.current) {
      this.textContainerRef.current.addEventListener('touchstart', this.handleTouchStart, false);
      this.textContainerRef.current.addEventListener('touchmove', this.onTochMove, false);
    }
    document.addEventListener('scroll', this.scrollListener);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isActive && this.state.isActive) {
      this.initTimer();
    }
    if (prevState.isActive && !this.state.isActive) {
      this.unmountTimer();
    }
    if (!prevProps.isActive && this.props.isActive) {
      this.initTimer();
    }
    if (prevProps.isActive && !this.props.isActive) {
      this.unmountTimer();
    }
  }

  componentWillUnmount() {
    this.unmountTimer();
    document.removeEventListener('scroll', this.scrollListener);
    if (this.textContainerRef.current) {
      this.textContainerRef.current.removeEventListener('touchstart', this.handleTouchStart, false);
      this.textContainerRef.current.removeEventListener('touchmove', this.onTochMove, false);
    }
  }

  onScroll = () => {
    if (!this.containerRef.current) {
      return;
    }
    const clientRect = this.containerRef.current.getBoundingClientRect();
    if (!clientRect) {
      return;
    }
    const elTop = clientRect.top;
    const elBottom = clientRect.bottom;
    if (elTop < 0 && elBottom > 0) {
      if (!this.state.isActive) {
        this.setState({isActive: true});
      }
    } else if (this.state.isActive) {
      this.setState({isActive: false});
    }
  };

  unmountTimer = () => {
    if (this.timer) {
      this.timer.clear();
    }
  };

  initTimer = () => {
    this.unmountTimer();
    this.timer = new Timer(this.changeSlide, slideDuration);
  };

  nextSlide = () => {
    this.timer.clear();
    const {slide} = this.state;
    const data = this.props.sectionData || {};
    const slides = data.slides || [];
    if (slides.length <= 1) {
      return;
    }
    const nextSlideIndex = slide + 1;
    const nextSlide = slides[nextSlideIndex];
    if (nextSlide) {
      this.setState({slide: nextSlideIndex});
    } else {
      this.setState({slide: 0});
    }
    this.initTimer();
  };

  prevSlide = () => {
    this.timer.clear();
    const {slide} = this.state;
    const data = this.props.sectionData || {};
    const slides = data.slides || [];
    const prevSlideIndex = slide - 1 >= 0 ? slide - 1 : slides.length - 1;
    const prevSlide = slides[prevSlideIndex];
    if (prevSlide) {
      this.setState({slide: prevSlideIndex});
    } else {
      this.setState({slide: 0});
    }
    this.initTimer();
  };

  pauseTimer = () => {
    this.timer.pause();
    this.setState({pause: true});
  };

  resumeTimer = () => {
    this.timer.resume();
    this.setState({pause: false});
  };

  changeSlide = () => {
    this.nextSlide();
  };

  onClickSlide = (index) => {
    this.timer.clear();
    this.setState({slide: index}, () => {
      this.initTimer();
    });
  };

  getCurrentSlideText = () => {
    const locale = this.props.language;
    const data = this.props.sectionData || {};
    const slides = data.slides || [];
    const currentSlide = slides[this.state.slide] || {};
    let text = '';
    let highlightedText = '';
    if (locale === 'ru') {
      highlightedText = currentSlide.highlightedTextRu || '';
      text = currentSlide.textRu || '';
    } else if (locale === 'en') {
      highlightedText = currentSlide.highlightedTextEn || '';
      text = currentSlide.textEn || '';
    }
    return `${highlightedText ? (`<mark>${highlightedText}</mark>`) : ''} ${formatText(text)}`;
  };

  handleTouchStart = (evt = {}) => {
    const firstTouch = evt.touches[0];
    this.xDown = firstTouch.clientX;
  };

  handleTouchMove = (evt) => {
    if (!this.textContainerRef.current.contains(evt.target)) {
      return;
    }
    const xUp = evt.touches[0].clientX;
    const xDiff = this.xDown - xUp;

    if (Math.abs(xDiff) > 100 && (xDiff > 0)) {
      this.prevSlide();
    }

    if (Math.abs(xDiff) > 100 && (xDiff < 0)) {
      this.nextSlide();
    }
  };

  render() {
    const data = this.props.sectionData || {};
    const slides = data.slides || [];
    const activeSlider = slides.length > 1;
    const sectionBackground = this.props.images;
    const isSlideCollection = Array.isArray(sectionBackground);
    const bgImage = `url(${isSlideCollection ? sectionBackground[this.state.slide] : this.props.images})`;
    const titleText = data.title ? data.title[this.props.language] : '';
    const subTitleText = data.subTitle ? data.subTitle[this.props.language] : '';
    const slogan = data.slogan ? data.slogan[this.props.language] : '';
    const {seeExampleVideo, howItWorksVideo} = data;

    return (
      <div
        className="section section--about"
        data-anchor={data.key}
        ref={this.containerRef}
      >
        {this.state.videoSrc && (
          <ModalVideoPlayer
            onClose={() => this.setState({videoSrc: null})}
            videoSrc={this.state.videoSrc}
          />
        )}
        <div
          className="section_background"
          style={{
            backgroundImage: bgImage,
          }}
        />
        <div className="section--about--btns_wrap">
          <div className="section--about--btns">
            <button
              type="button"
              onClick={() => this.setState({videoSrc: howItWorksVideo})}
              className={cl('btn btn-base', {
                disabled: !howItWorksVideo,
              })}
              disabled={!howItWorksVideo}
            >
              {t('buttons.howItWork')}
            </button>
            <button
              type="button"
              onClick={() => this.setState({videoSrc: seeExampleVideo})}
              className={cl('btn btn-base', {
                disabled: !seeExampleVideo,
              })}
              disabled={!seeExampleVideo}
            >
              {t('buttons.watchExample')}
            </button>
          </div>
        </div>
        <div className="section--inner">
          <div className="section--container">
            <div className="section--block">
              <div className="section_content">
                <div
                  className="carousel-big"
                  onMouseEnter={activeSlider ? this.pauseTimer : null}
                  onMouseLeave={activeSlider ? this.resumeTimer : null}
                >
                  {slogan && (<div className="section--slogan">{slogan}</div>)}
                  {titleText && (<div className="section--title">{titleText}</div>)}
                  {subTitleText && (<div className="section--subtitle">{subTitleText}</div>)}
                  <div
                    className={cl('section--text', {
                      'can-swipe': activeSlider,
                    })}
                    ref={this.textContainerRef}
                  >
                    <p
                      dangerouslySetInnerHTML={{__html: this.getCurrentSlideText()}}
                    />
                    {activeSlider && (
                      <>
                        <button
                          type="button"
                          onClick={this.prevSlide}
                          className="carousel-big--navigation_btn carousel-prev"
                        >
                          <i className="fa fa-angle-left" />
                        </button>
                        <button
                          type="button"
                          onClick={this.nextSlide}
                          className="carousel-big--navigation_btn carousel-next"
                        >
                          <i className="fa fa-angle-right" />
                        </button>
                      </>
                    )}
                  </div>
                  {activeSlider && (
                    <div className="carousel-small--navigation">
                      <ul>
                        {slides.map((slide, index) => (
                          <li
                            key={index}
                            className={cl('', {
                              filled: index === this.state.slide && (this.state.isActive || this.props.isActive),
                            })}
                            onClick={() => this.onClickSlide(index)}
                          >
                            <span
                              className={this.state.pause ? 'pause' : ''}
                              style={{animationDuration: `${slideDuration}ms`}}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="section--about--btn-wrap">
                  <Link to={ROUTES.FEED.route} className="btn btn-base btn-login">
                    {t('buttons.tryFree')}
                    <ArrowForwardIcon />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

LandingPageAboutSection.defaultProps = {
  language: 'ru',
  isActive: false,
};

LandingPageAboutSection.propTypes = {
  language: PropTypes.string,
  isActive: PropTypes.bool,
};

export default LandingPageAboutSection;
